// Step 4 — Acts & Limits (Lines 4, 5a, 5b, 6)
import React from 'react';
import type { POAFormState } from '../../../types/poa';

interface Props {
  form: POAFormState;
  onChange: (field: keyof POAFormState, value: boolean | string) => void;
}

function CheckRow({
  checked, onChange, label, description,
}: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string }) {
  return (
    <label style={{ display: 'flex', gap: 10, marginBottom: 14, cursor: 'pointer', alignItems: 'flex-start' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, flexShrink: 0 }}
      />
      <div>
        <div style={{ fontSize: 14 }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{description}</div>}
      </div>
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'rgba(255,255,255,.04)', color: 'var(--text)',
  fontSize: 14, outline: 'none',
};

export default function ActsStep({ form, onChange }: Props) {
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
        All fields on this step are optional. The acts on Line 3 (receive and inspect tax information,
        sign agreements and consents) are granted by default. Check the boxes below only if additional
        authority is needed.
      </p>

      {/* Line 4 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hd"><span style={{ fontWeight: 600, fontSize: 13 }}>Line 4 — Specific Use Not Recorded on CAF</span></div>
        <div className="bd">
          <CheckRow
            checked={form.isSpecificUseNotOnCaf}
            onChange={(v) => onChange('isSpecificUseNotOnCaf', v)}
            label="This is a specific use not recorded on the Centralized Authorization File (CAF)"
            description="Check this box if the POA is for a limited-scope matter (e.g., a single IRS interview or document request). The IRS will not record it on the CAF system."
          />
        </div>
      </div>

      {/* Line 5a */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hd"><span style={{ fontWeight: 600, fontSize: 13 }}>Line 5a — Additional Acts Authorized</span></div>
        <div className="bd">
          <CheckRow
            checked={form.authIntermediateServiceProvider}
            onChange={(v) => onChange('authIntermediateServiceProvider', v)}
            label="Access IRS records via an Intermediate Service Provider"
            description="Authorizes representative to retrieve tax data through third-party software/service providers."
          />
          <CheckRow
            checked={form.authDisclosureThirdParties}
            onChange={(v) => onChange('authDisclosureThirdParties', v)}
            label="Authorize disclosure to third parties"
          />
          <CheckRow
            checked={form.authSubstituteAddRep}
            onChange={(v) => onChange('authSubstituteAddRep', v)}
            label="Substitute or add a representative"
            description="Allows the listed representative to delegate authority to another practitioner."
          />
          <CheckRow
            checked={form.authSignReturn}
            onChange={(v) => onChange('authSignReturn', v)}
            label="Sign a return"
            description="Only permitted in limited circumstances: disease/injury preventing taxpayer from signing, absence of more than 60 days, or explicit IRS permission."
          />
          <div style={{ marginTop: 4 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>
              Other acts authorized (describe):
            </label>
            <input
              style={inputStyle}
              value={form.authOtherActs}
              placeholder="Describe any other authorized acts"
              onChange={(e) => onChange('authOtherActs', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Line 5b */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hd"><span style={{ fontWeight: 600, fontSize: 13 }}>Line 5b — Specific Acts NOT Authorized</span></div>
        <div className="bd">
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
            Representatives are <strong>never</strong> authorized to negotiate or endorse any IRS-issued
            check on behalf of the taxpayer (this restriction is automatic and cannot be waived).
          </div>
          <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>
            Additional acts to restrict (describe):
          </label>
          <input
            style={inputStyle}
            value={form.unauthOtherDeletions}
            placeholder="Describe any additional restrictions"
            onChange={(e) => onChange('unauthOtherDeletions', e.target.value)}
          />
        </div>
      </div>

      {/* Line 6 */}
      <div className="card">
        <div className="hd"><span style={{ fontWeight: 600, fontSize: 13 }}>Line 6 — Retention of Prior POA(s)</span></div>
        <div className="bd">
          <CheckRow
            checked={form.retainPriorPoa}
            onChange={(v) => onChange('retainPriorPoa', v)}
            label="Retain prior power(s) of attorney on file for the same matters and years"
            description="By default, filing a new Form 2848 automatically revokes prior POAs covering the same matters and tax years. Check this box ONLY if you want prior POAs to remain in effect, and attach a copy of each POA you want to keep."
          />
        </div>
      </div>
    </div>
  );
}
